/**
 * penalesLogic.js — Lógica pura del minijuego de penales.
 * Easter eggs:
 *   1. Patrón 18 tiros — el arquero cubre exactamente 3 veces cada zona disponible
 *   2. "El Analista"  — 10 goles seguidos en la misma zona
 *   3. Penal Potenciado  — tecla + activa 2 tiros invencibles (1 vez por sesión)
 */
import { supabase } from '../../../supabaseClient';

export const ZONAS = [
  { id: 0, label: 'Arriba Izquierda', row: 'top',    col: 'left'   },
  { id: 1, label: 'Arriba Centro',    row: 'top',    col: 'center' },
  { id: 2, label: 'Arriba Derecha',   row: 'top',    col: 'right'  },
  { id: 3, label: 'Abajo Izquierda',  row: 'bottom', col: 'left'   },
  { id: 4, label: 'Abajo Centro',     row: 'bottom', col: 'center' },
  { id: 5, label: 'Abajo Derecha',    row: 'bottom', col: 'right'  },
];

export const getZonasDisponibles = (streak) => {
  let eliminadas = [];
  if (streak >= 10) eliminadas.push(1); // al 10 desaparece arriba-centro
  if (streak >= 20) eliminadas.push(4); // al 20 desaparece abajo-centro
  if (streak >= 30) eliminadas.push(2); // al 30 desaparece arriba-derecha
  return ZONAS.filter(z => !eliminadas.includes(z.id));
};

export const getDificultad      = (s) => s >= 30 ? 'extreme' : s >= 10 ? 'hard' : 'normal';
export const getDificultadLabel = (s) => s >= 30 ? 'EXTREMO' : s >= 20 ? 'DIFÍCIL+' : s >= 10 ? 'DIFÍCIL' : 'NORMAL';

// ── Patrón de 18 tiros ────────────────────────────────────────────────────────
// Genera una secuencia donde cada zona aparece exactamente 3 veces.
// Respeta los bloqueos: zona 1 solo puede aparecer en tiros 1-10, zona 4 en 1-20.
// Se genera UNA VEZ por sesión y se consume en orden.
const generarPatron18 = () => {
  // Tiros 1-9: zonas 0,1,2,3,4,5 disponibles
  // Tiro 10:   zona 1 se bloquea → últimas apariciones de zona 1 deben ser antes o en tiro 10
  // Tiros 11-18: zonas 0,2,3,4,5 (sin zona 1)

  // Estrategia: asegurar las 3 visitas a zona 1 en los primeros 9 tiros,
  // y las 3 visitas a zona 4 antes del tiro 20 (fácil, los 18 están antes del 20)
  const zonasPhase1 = [0, 1, 2, 3, 4, 5]; // tiros 1-9 (zona 1 disponible)
  const zonasPhase2 = [0, 2, 3, 4, 5];    // tiros 10-18 (sin zona 1)

  // Armar pool: zona 1 aparece 3 veces en phase1, resto se distribuye
  // Phase 1: 9 tiros. Zona 1 → 3 veces. Resto (0,2,3,4,5) → 6 tiros entre 5 zonas
  const pool1 = [1, 1, 1]; // las 3 obligatorias de zona 1
  // Completamos los 6 restantes de phase1 con las otras zonas
  // Contamos cuántas de cada zona necesitamos en total: 3 por zona
  // Zona 1: 3 en phase1, 0 en phase2 ✓
  // Zonas 0,2,3,4,5: deben sumar 3 en total (entre phase1 y phase2)
  // Phase2 tiene 9 tiros con 5 zonas → tocan ~1.8 veces cada una
  // Para que sea exactamente 3: en phase1 ponemos algunas y en phase2 completamos

  // Distribución exacta:
  // Phase1 (9 tiros): zona1×3, y 6 tiros más con zonas 0,2,3,4,5
  // Phase2 (9 tiros): zonas 0,2,3,4,5 — exactamente lo que falte para llegar a 3 cada una

  // Shuffleamos zona 1 entre las 9 posiciones de phase1
  const resto1 = [];
  // Cada zona 0,2,3,4,5 necesita 3 visitas totales, distribuidas entre phase1 y phase2
  // Ponemos 1 de cada una en phase1 (5 zonas × 1 = 5) → queda 1 spot en phase1
  // Ese último spot lo sortemos entre las 5 zonas restantes
  [0, 2, 3, 4, 5].forEach(z => resto1.push(z)); // 5 zonas × 1 vez = 5
  // El 9° slot de phase1: una zona al azar que aún necesite 2 más en phase2
  const bonusZona = [0, 2, 3, 4, 5][Math.floor(Math.random() * 5)];
  resto1.push(bonusZona);

  // Phase1 = [1,1,1] + resto1 shuffleado
  const phase1 = [...pool1, ...resto1].sort(() => Math.random() - 0.5);

  // Phase2: completar las visitas restantes para zonas 0,2,3,4,5 hasta llegar a 3 cada una
  const conteoPhase1 = {};
  phase1.forEach(z => { conteoPhase1[z] = (conteoPhase1[z] || 0) + 1; });
  const phase2 = [];
  [0, 2, 3, 4, 5].forEach(z => {
    const faltan = 3 - (conteoPhase1[z] || 0);
    for (let i = 0; i < faltan; i++) phase2.push(z);
  });
  phase2.sort(() => Math.random() - 0.5);

  return [...phase1, ...phase2]; // 18 elementos
};

// Estado del patrón — se inicializa una vez por juego
let patronActual = null;
let patronIndex  = 0;

// Último lado usado en la fase 19-25 (para no repetir)
let lastLado = null;

export const resetPatron = () => {
  patronActual = generarPatron18();
  patronIndex  = 0;
  lastLado     = null;
};

// ── Ejecutar disparo ─────────────────────────────────────────────────────────
export const ejecutarDisparo = (zonaElegidaId, zonasDisponibles, totalShots, potenciado = false) => {
  // Tiro 0: primer penal — siempre gol, no consume el patrón
  if (totalShots === 0) {
    const zonasOtras = zonasDisponibles.filter(z => z.id !== zonaElegidaId);
    const zonaArquero = zonasOtras.length > 0
      ? zonasOtras[Math.floor(Math.random() * zonasOtras.length)]
      : zonasDisponibles[0];
    return { gol: true, zonaArqueroId: zonaArquero.id };
  }

  // Tiros 1-18: patrón distribuido
  // El potenciado también respeta el patrón — el arquero va donde dice el patrón pero falla
  if (totalShots >= 1 && totalShots <= 18) {
    if (!patronActual || patronIndex >= 18) {
      patronActual = generarPatron18();
      patronIndex  = 0;
    }
    const zonaArqueroId = patronActual[patronIndex];
    patronIndex++;
    const zonaDisp = zonasDisponibles.find(z => z.id === zonaArqueroId);
    if (!zonaDisp) {
      const fallback = zonasDisponibles[Math.floor(Math.random() * zonasDisponibles.length)];
      // Si potenciado: gol igual aunque el arquero esté en la zona correcta
      return { gol: potenciado ? true : fallback.id !== zonaElegidaId, zonaArqueroId: fallback.id };
    }
    // Si potenciado: gol garantizado aunque el arquero vaya al mismo lugar
    return { gol: potenciado ? true : zonaArqueroId !== zonaElegidaId, zonaArqueroId };
  }

  // Tiros 19-25: izq/der alternando — nunca repite el mismo lado consecutivo
  if (totalShots >= 19 && totalShots <= 25) {
    // Determinar qué lado toca
    const ladoElegido = lastLado === 'left' ? 'right' : lastLado === 'right' ? 'left' : (Math.random() < 0.5 ? 'left' : 'right');
    lastLado = ladoElegido;
    // Zonas de ese lado (arriba o abajo al azar)
    const zonasLado = zonasDisponibles.filter(z => z.col === ladoElegido);
    const pool = zonasLado.length > 0 ? zonasLado : zonasDisponibles;
    const zonaArquero = pool[Math.floor(Math.random() * pool.length)];
    return { gol: zonaArquero.id !== zonaElegidaId, zonaArqueroId: zonaArquero.id };
  }

  // Del 26 en adelante: totalmente al azar
  let zonas = zonasDisponibles;
  if (zonaElegidaId === 4) {
    zonas = zonasDisponibles.filter(z => z.id !== 1);
    if (zonas.length === 0) zonas = zonasDisponibles;
  }
  const zonaArquero = zonas[Math.floor(Math.random() * zonas.length)];
  return { gol: potenciado ? true : zonaArquero.id !== zonaElegidaId, zonaArqueroId: zonaArquero.id };
};

// ── Leer/guardar récord ───────────────────────────────────────────────────────
export const leerMejorRacha = async (userId) => {
  const { data } = await supabase.from('perfiles').select('racha_penales').eq('id', userId).single();
  return data?.racha_penales || 0;
};

const HITOS_PENALES = [
  { n: 5,  subtipo: 'goles_5',  emoji: '5penales.png',  nombre: '5 Goles',  desc: 'Marcaste 5 goles seguidos en penales'  },
  { n: 10, subtipo: 'goles_10', emoji: '10penales.png', nombre: '10 Goles', desc: 'Marcaste 10 goles seguidos en penales' },
  { n: 20, subtipo: 'goles_20', emoji: '20penales.png', nombre: '20 Goles', desc: 'Marcaste 20 goles seguidos en penales' },
  { n: 30, subtipo: 'goles_30', emoji: '30penales.png', nombre: '30 Goles', desc: 'Marcaste 30 goles seguidos en penales' },
  { n: 40, subtipo: 'goles_40', emoji: '40penales.png', nombre: '40 Goles', desc: 'Marcaste 40 goles seguidos en penales' },
];

// Medalla secreta — El Analista
const MEDALLA_ANALISTA = {
  subtipo:     'el_analista',
  emoji:       '🔬',
  nombre:      'El Analista',
  descripcion: 'Metiste 10 goles seguidos en la misma zona. El patrón era tuyo.',
  tipo:        'jugada_preparada',
};

export const guardarMejorRacha = async (userId, racha) => {
  const { data: perfil } = await supabase.from('perfiles').select('racha_penales').eq('id', userId).single();
  const mejorActual = perfil?.racha_penales || 0;
  if (racha <= mejorActual) return false;

  await supabase.from('perfiles').update({ racha_penales: racha }).eq('id', userId);

  for (const h of HITOS_PENALES) {
    if (racha >= h.n && mejorActual < h.n) {
      const { count } = await supabase.from('medallas_usuario')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId).eq('subtipo', h.subtipo);
      if (!count) {
        await supabase.from('medallas_usuario').insert({
          user_id: userId, tipo: 'hito', subtipo: h.subtipo,
          emoji: h.emoji, nombre: h.nombre, descripcion: h.desc, visible: true,
        });
        await supabase.from('notificaciones').insert({
          user_id: userId, tipo: 'medalla',
          texto: `Nuevo hito: ${h.nombre}`, emoji: h.emoji, leida: false,
        });
      }
    }
  }
  return true;
};

// Medalla El Analista — 10 goles seguidos en la misma zona
export const verificarAnalista = async (userId, historicoZonas) => {
  // historicoZonas: array de ids de zona donde se metió gol (en orden)
  if (historicoZonas.length < 10) return false;
  const ultimas10 = historicoZonas.slice(-10);
  const todasIguales = ultimas10.every(z => z === ultimas10[0]);
  if (!todasIguales) return false;

  // Ver si ya tiene la medalla
  const { count } = await supabase.from('medallas_usuario')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId).eq('subtipo', MEDALLA_ANALISTA.subtipo);
  if (count > 0) return false;

  await supabase.from('medallas_usuario').insert({
    user_id: userId, tipo: MEDALLA_ANALISTA.tipo,
    subtipo: MEDALLA_ANALISTA.subtipo, emoji: MEDALLA_ANALISTA.emoji,
    nombre: MEDALLA_ANALISTA.nombre, descripcion: MEDALLA_ANALISTA.descripcion,
    visible: false, // oculta — jugada preparada
  });
  await supabase.from('notificaciones').insert({
    user_id: userId, tipo: 'jugada_preparada',
    texto: `¡Desbloqueaste una Jugada Preparada! 🔬 El Analista`,
    emoji: '🔬', leida: false,
  });
  return true;
};